// src/pages/WarrantyApp.jsx
import React, { useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Outlet } from "react-router-dom";

import AppHeader from "../Layout/AppHeader";
import BottomNav from "./components/warranty/BottomNav";

import ClaimWizardDialog from "./components/dialogs/ClaimWizardDialog";
import AddressDialog from "./components/dialogs/AddressDialog";
import PurchasesDialog from "./components/dialogs/PurchasesDialog";
import DeviceDetailsDialog from "./components/dialogs/DeviceDetailsDialog";

import { AuthContext } from "./context/AuthContext";
import { BASE_URL } from "./config/api";
import axios from "axios";
import ConfirmDialog from "./components/dialogs/ConfirmDialog";
import ClaimDetailsDialog from "./components/dialogs/ClaimDetailsDialog";

export default function WarrantyApp() {
  const { user, logout } = useContext(AuthContext);
  const customerId = user?.id;

  /* ---------------- GLOBAL UI STATE ---------------- */

  const [bootLoading, setBootLoading] = useState(true);

  const [openClaim, setOpenClaim] = useState(false);
  const [preselectedDeviceId, setPreselectedDeviceId] = useState(null);

  const [addresses, setAddresses] = useState([]);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [addressMode, setAddressMode] = useState("add");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);

  const [openPurchases, setOpenPurchases] = useState(false);
  const [activePurchaseId, setActivePurchaseId] = useState(null);

  const [openDeviceDetails, setOpenDeviceDetails] = useState(false);
  const [activeDeviceId, setActiveDeviceId] = useState(null);

  const [activeClaim, setActiveClaim] = useState(null);

  const [deleteAddressId, setDeleteAddressId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [retailerInfo, setRetailerInfo] = useState(null);

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  const [claims, setClaims] = useState([]);
  const [claimsMeta, setClaimsMeta] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [openClaimDetails, setOpenClaimDetails] = useState(false);

  const globalLoading = bootLoading || devicesLoading || claimsLoading;

  const purchases = [
    {
      id: "pur-1",
      model_name: "Google Pixel 8",
      purchase_date: "2025-12-20",
      order_no: "ORD-77881",
      serial_no: "SN-PXL8-88991234",
      imei: "356789101112131",
      warranty_expiry: "2026-12-20",
      purchased_from: { name: "Akash Mobile World" },
    },
    {
      id: "pur-2",
      model_name: "Samsung Galaxy S24",
      purchase_date: "2025-11-08",
      order_no: "ORD-66420",
      serial_no: "SN-S24-10012025",
      imei: "357891011121314",
      warranty_expiry: "2026-11-08",
      purchased_from: { name: "City Mobiles" },
    },
  ];

  /* ---------------- DERIVED DATA ---------------- */

  const fetchClaims = async () => {
    if (!customerId) return;

    try {
      setClaimsLoading(true);

      const res = await axios.get(`${BASE_URL}warranty/claims/list`, {
        params: { w_customer_id: customerId },
      });

      if (res.data?.status) {
        const payload = res.data.data;

        setClaims(payload.data || []);
        setClaimsMeta({
          currentPage: payload.current_page,
          lastPage: payload.last_page,
          total: payload.total,
          perPage: payload.per_page,
        });
      } else {
        setClaims([]);
        setClaimsMeta(null);
      }
    } catch (err) {
      console.error(
        "Failed to fetch claims",
        err?.response?.data || err.message
      );
      setClaims([]);
      setClaimsMeta(null);
    } finally {
      setClaimsLoading(false);
    }
  };

  const claimRecords = claims;

  const activeDevice = devices.find((d) => d.id === activeDeviceId);
  const activeDevicePurchase = purchases.find(
    (p) => p.id === activeDevice?.purchase_id
  );

  const deviceClaims = claimRecords.filter(
    (c) => Number(c.w_device_id) === Number(activeDeviceId)
  );

  const activeClaimDevice = devices.find(
    (d) =>
      Number(d.id) ===
      Number(activeClaim?.w_device_id || activeClaim?.device_id)
  );

  const activeClaimPurchase = purchases.find(
    (p) => Number(p.id) === Number(activeClaimDevice?.purchase_id)
  );

  const normalizeClaim = (claim) => ({
    ...claim,
    issues: claim.issue_description
      ? claim.issue_description.split(",").map((s) => s.trim())
      : [],
    status_history: [
      {
        key: claim.status,
        at: claim.updated_at,
      },
    ],
    payment: null,
  });

  /* ---------------- ACTIONS ---------------- */

  const startClaim = (deviceId) => {
    if (deviceId) {
      setPreselectedDeviceId(deviceId);
    } else {
      setPreselectedDeviceId(null);
    }
    setOpenClaim(true);
  };

  const openAddAddress = () => {
    setAddressMode("add");
    setSelectedAddress(null);
    setOpenAddressDialog(true);
  };

  const openEditAddress = (address) => {
    setAddressMode("edit");
    setSelectedAddress(address);
    setOpenAddressDialog(true);
  };

  const openPurchaseList = (purchaseId = null) => {
    setActivePurchaseId(purchaseId);
    setOpenPurchases(true);
  };

  const openDeviceDetailsById = (id) => {
    setActiveDeviceId(id);
    setOpenDeviceDetails(true);
  };

  const handleOpenClaimDetails = (claim) => {
    setActiveClaim(normalizeClaim(claim));
    setOpenClaimDetails(true);
  };

  const fetchAddresses = async () => {
    if (!customerId) return;

    try {
      const res = await axios.post(
        `${BASE_URL}warranty/customer/address/list`,
        {
          w_customer_id: customerId,
        }
      );

      if (res.data?.status) {
        setAddresses(res.data.data || []);
      }
    } catch (err) {
      console.error(
        "Failed to fetch addresses",
        err?.response?.data || err.message
      );
    }
  };

  const fetchCustomerDevices = async () => {
    if (!customerId) return;

    try {
      setDevicesLoading(true);

      const res = await axios.get(`${BASE_URL}warranty/get-customer-details`, {
        params: { id: customerId },
      });

      const data = res.data;

      if (data && !Array.isArray(data)) {
        setCustomerInfo(data);
        setRetailerInfo(data.retailer || null);
        setDevices(data.devices || []);
        return;
      }

      if (Array.isArray(data)) {
        setCustomers(data);

        const currentCustomer = data.find(
          (c) => Number(c.id) === Number(customerId)
        );

        setCustomerInfo(currentCustomer || null);
        setRetailerInfo(currentCustomer?.retailer || null);
        setDevices(currentCustomer?.devices || []);
        return;
      }

      setCustomerInfo(null);
      setRetailerInfo(null);
      setDevices([]);
    } catch (err) {
      console.error(
        "Failed to fetch customer details",
        err?.response?.data || err.message
      );
      setCustomerInfo(null);
      setRetailerInfo(null);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleSaveAddress = async (payload) => {
    try {
      setSavingAddress(true);

      if (addressMode === "add") {
        const res = await axios.post(
          `${BASE_URL}warranty/customer/address/create`,
          {
            w_customer_id: customerId,
            ...payload,
          }
        );

        if (res.data?.status) {
          await fetchAddresses();
          setOpenAddressDialog(false);
        }
      } else {
        const res = await axios.post(
          `${BASE_URL}warranty/customer/address/update`,
          {
            address_id: selectedAddress.id,
            ...payload,
          }
        );

        if (res.data?.status) {
          setAddresses((prev) =>
            prev.map((a) =>
              a.id === selectedAddress.id ? { ...a, ...payload } : a
            )
          );
          setOpenAddressDialog(false);
        }
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to save address");
    } finally {
      setSavingAddress(false);
    }
  };

  const confirmDeleteAddress = (id) => {
    setDeleteAddressId(id);
  };

  const handleDeleteAddress = async () => {
    if (!deleteAddressId) return;

    try {
      setDeleting(true);

      await axios.post(`${BASE_URL}warranty/customer/address/delete`, {
        address_id: deleteAddressId,
      });

      await fetchAddresses();
      setDeleteAddressId(null);
    } catch (err) {
      alert("Failed to delete address");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------------- BOOT ---------------- */

  useEffect(() => {
    if (!customerId) return;

    fetchAddresses();
    fetchCustomerDevices();
    fetchClaims();

    const t = setTimeout(() => setBootLoading(false), 500);
    return () => clearTimeout(t);
  }, [customerId]);

  /* ---------------- RENDER ---------------- */

  if (!user) return null;

  return (
    <>
      {globalLoading && <GlobalLoader />}

      {/* ✨ APP SHELL — Warranty Mitra green/off-white canvas */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          // Soft green-tinted background matching reference design
          background:
            "linear-gradient(180deg, #f0fdf4 0%, #f8faf9 50%, #f9fafb 100%)",
          // Decorative blurred green blobs
          "&::before": {
            content: '""',
            position: "fixed",
            top: 80,
            right: -140,
            width: 340,
            height: 340,
            borderRadius: "5%",
            background:
              "radial-gradient(circle, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0) 70%)",
            filter: "blur(10px)",
            pointerEvents: "none",
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "fixed",
            bottom: 100,
            left: -140,
            width: 360,
            height: 360,
            borderRadius: "5%",
            background:
              "radial-gradient(circle, rgba(21,128,61,0.14) 0%, rgba(21,128,61,0) 70%)",
            filter: "blur(10px)",
            pointerEvents: "none",
            zIndex: 0,
          },
        }}
      >
        {/* HEADER */}
        <AppHeader user={user} onLogout={logout} />

        {/* ROUTED CONTENT */}
        <Box
          component="main"
          sx={{
            flex: 1,
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 560, // phone-first container
            mx: "auto",
            px: { xs: 2, sm: 2.5 },
            pt: { xs: 2, sm: 2.5 },
            pb: "96px", // space for bottom nav
            overflowY: "auto",
            // Subtle fade-in on mount
            animation: "fadeInUp 400ms ease-out",
            "@keyframes fadeInUp": {
              from: { opacity: 0, transform: "translateY(8px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Outlet
            context={{
              bootLoading,
              devices,
              claims,
              claimsLoading,
              customerInfo,
              retailerInfo,
              addresses,
              onRaiseClaim: startClaim,
              onOpenClaimDetails: handleOpenClaimDetails,
              onOpenDevice: openDeviceDetailsById,
              onViewPurchase: openPurchaseList,
              onAddAddress: openAddAddress,
              onEditAddress: openEditAddress,
              onDeleteAddress: confirmDeleteAddress,
            }}
          />
        </Box>

        {/* BOTTOM NAV (URL BASED) */}
        <BottomNav />

        {/* ---------------- DIALOGS ---------------- */}

        <ClaimWizardDialog
          open={openClaim}
          preselectedDeviceId={preselectedDeviceId}
          onClose={() => setOpenClaim(false)}
          devices={devices}
          addresses={addresses}
          retailerInfo={retailerInfo}
          channelPartners={[]}
          onSubmit={async () => {
            setOpenClaim(false);
            await fetchClaims();
          }}
        />

        <AddressDialog
          open={openAddressDialog}
          mode={addressMode}
          initialData={selectedAddress}
          saving={savingAddress}
          onClose={() => setOpenAddressDialog(false)}
          onSave={handleSaveAddress}
        />

        <PurchasesDialog
          open={openPurchases}
          onClose={() => setOpenPurchases(false)}
          purchases={devices}
          retailerInfo={retailerInfo}
          activePurchaseId={activePurchaseId}
        />

        <DeviceDetailsDialog
          open={openDeviceDetails}
          onClose={() => setOpenDeviceDetails(false)}
          device={activeDevice}
          retailerInfo={retailerInfo}
          deviceClaims={deviceClaims}
          onRaiseClaim={(id) => {
            setOpenDeviceDetails(false);
            startClaim(id);
          }}
          onOpenClaim={(claim) => {
            setOpenDeviceDetails(false);
            handleOpenClaimDetails(claim);
          }}
        />

        <ConfirmDialog
          open={Boolean(deleteAddressId)}
          title="Delete Address?"
          description="This address will be permanently removed. This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          loading={deleting}
          onClose={() => setDeleteAddressId(null)}
          onConfirm={handleDeleteAddress}
        />

        <ClaimDetailsDialog
          open={openClaimDetails}
          onClose={() => setOpenClaimDetails(false)}
          claim={activeClaim}
          device={activeClaim?.device}
        />
      </Box>
    </>
  );
}

/* ---------------- GLOBAL LOADER ---------------- */

function GlobalLoader() {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, rgba(240,253,244,0.78) 0%, rgba(248,250,249,0.78) 100%)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          px: 4,
          py: 3,
          borderRadius: 0.5,
          bgcolor: "rgba(255,255,255,0.92)",
          boxShadow: "0 12px 40px rgba(21,128,61,0.18)",
          border: "1px solid rgba(220,252,231,0.8)",
        }}
      >
        <CircularProgress size={38} thickness={4} sx={{ color: "#15803d" }} />
        <Typography
          variant="body2"
          sx={{
            fontFamily: '"Sora", sans-serif',
            fontWeight: 700,
            color: "#15803d",
            letterSpacing: 0.2,
          }}
        >
          Loading, please wait…
        </Typography>
      </Box>
    </Box>
  );
}

// // src/pages/WarrantyApp.jsx
// import React, { useContext, useEffect, useState } from "react";
// import { Box } from "@mui/material";
// import { Outlet } from "react-router-dom";

// import AppHeader from "../Layout/AppHeader";
// import BottomNav from "./components/warranty/BottomNav";

// import ClaimWizardDialog from "./components/dialogs/ClaimWizardDialog";
// import AddressDialog from "./components/dialogs/AddressDialog";
// import PurchasesDialog from "./components/dialogs/PurchasesDialog";
// import DeviceDetailsDialog from "./components/dialogs/DeviceDetailsDialog";

// import { AuthContext } from "./context/AuthContext";
// import { BASE_URL } from "./config/api";
// import axios from "axios";
// import ConfirmDialog from "./components/dialogs/ConfirmDialog";
// import ClaimDetailsDialog from "./components/dialogs/ClaimDetailsDialog";

// export default function WarrantyApp() {
//   const { user, logout } = useContext(AuthContext);
//   const customerId = user?.id;

//   /* ---------------- GLOBAL UI STATE ---------------- */

//   const [bootLoading, setBootLoading] = useState(true);

//   const [openClaim, setOpenClaim] = useState(false);
//   const [preselectedDeviceId, setPreselectedDeviceId] = useState(null);

//   const [addresses, setAddresses] = useState([]);
//   const [openAddressDialog, setOpenAddressDialog] = useState(false);
//   const [addressMode, setAddressMode] = useState("add");
//   const [selectedAddress, setSelectedAddress] = useState(null);
//   const [savingAddress, setSavingAddress] = useState(false);

//   const [openPurchases, setOpenPurchases] = useState(false);
//   const [activePurchaseId, setActivePurchaseId] = useState(null);

//   const [openDeviceDetails, setOpenDeviceDetails] = useState(false);
//   const [activeDeviceId, setActiveDeviceId] = useState(null);

//   const [activeClaim, setActiveClaim] = useState(null);

//   const [deleteAddressId, setDeleteAddressId] = useState(null);
//   const [deleting, setDeleting] = useState(false);


//   const [customers, setCustomers] = useState([]);   // full customer list
//   const [customerInfo, setCustomerInfo] = useState(null); // logged-in customer
//   const [retailerInfo, setRetailerInfo] = useState(null); // retailer details

//   const [devices, setDevices] = useState([]);
//   const [devicesLoading, setDevicesLoading] = useState(false);

//   const [claims, setClaims] = useState([]);
//   const [claimsMeta, setClaimsMeta] = useState(null);
//   const [claimsLoading, setClaimsLoading] = useState(false);
//   const [openClaimDetails, setOpenClaimDetails] = useState(false);

//   const globalLoading =
//   bootLoading || devicesLoading || claimsLoading;

//   const purchases = [
//     {
//       id: "pur-1",
//       model_name: "Google Pixel 8",
//       purchase_date: "2025-12-20",
//       order_no: "ORD-77881",
//       serial_no: "SN-PXL8-88991234",
//       imei: "356789101112131",
//       warranty_expiry: "2026-12-20",
//       purchased_from: { name: "Akash Mobile World" },
//     },
//     {
//       id: "pur-2",
//       model_name: "Samsung Galaxy S24",
//       purchase_date: "2025-11-08",
//       order_no: "ORD-66420",
//       serial_no: "SN-S24-10012025",
//       imei: "357891011121314",
//       warranty_expiry: "2026-11-08",
//       purchased_from: { name: "City Mobiles" },
//     },
//   ];
//   /* ---------------- DERIVED DATA ---------------- */

//   const fetchClaims = async () => {
//     if (!customerId) return;

//     try {
//       setClaimsLoading(true);

//       const res = await axios.get(
//         `${BASE_URL}warranty/claims/list`,
//         {
//           params: { w_customer_id: customerId },
//         }
//       );

//       if (res.data?.status) {
//         const payload = res.data.data;

//         setClaims(payload.data || []);
//         setClaimsMeta({
//           currentPage: payload.current_page,
//           lastPage: payload.last_page,
//           total: payload.total,
//           perPage: payload.per_page,
//         });
//       } else {
//         setClaims([]);
//         setClaimsMeta(null);
//       }
//     } catch (err) {
//       console.error(
//         "Failed to fetch claims",
//         err?.response?.data || err.message
//       );
//       setClaims([]);
//       setClaimsMeta(null);
//     } finally {
//       setClaimsLoading(false);
//     }
//   };

//   const claimRecords = claims;

//   const activeDevice = devices.find((d) => d.id === activeDeviceId);
//   const activeDevicePurchase = purchases.find(
//     (p) => p.id === activeDevice?.purchase_id
//   );

//   const deviceClaims = claimRecords.filter(
//     (c) => Number(c.w_device_id) === Number(activeDeviceId)
//   );

// //   const activeClaim = claims.find(
// //   (c) => Number(c.id) === Number(activeClaimId)
// // );

// const activeClaimDevice = devices.find(
//   (d) => Number(d.id) === Number(activeClaim?.w_device_id || activeClaim?.device_id)
// );

// const activeClaimPurchase = purchases.find(
//   (p) => Number(p.id) === Number(activeClaimDevice?.purchase_id)
// );

// const normalizeClaim = (claim) => ({
//   ...claim,

//   // 🔹 convert issue_description → issues[]
//   issues: claim.issue_description
//     ? claim.issue_description.split(",").map(s => s.trim())
//     : [],

//   // 🔹 fake timeline from status
//   status_history: [
//     {
//       key: claim.status,
//       at: claim.updated_at,
//     },
//   ],

//   // 🔹 no payment yet
//   payment: null,
// });



//   /* ---------------- ACTIONS ---------------- */

//   const startClaim = (deviceId) => {
//   if (deviceId) {
//     setPreselectedDeviceId(deviceId);
//   } else {
//     setPreselectedDeviceId(null);
//   }
//   setOpenClaim(true);
// };


//   const openAddAddress = () => {
//     setAddressMode("add");
//     setSelectedAddress(null);
//     setOpenAddressDialog(true);
//   };

//   const openEditAddress = (address) => {
//     setAddressMode("edit");
//     setSelectedAddress(address);
//     setOpenAddressDialog(true);
//   };

//   const openPurchaseList = (purchaseId = null) => {
//     setActivePurchaseId(purchaseId);
//     setOpenPurchases(true);
//   };

//   const openDeviceDetailsById = (id) => {
//     setActiveDeviceId(id);
//     setOpenDeviceDetails(true);
//   };

//   const handleOpenClaimDetails = (claim) => {
//     setActiveClaim(normalizeClaim(claim));
//     setOpenClaimDetails(true);
//   };

//   const fetchAddresses = async () => {
//   if (!customerId) return;

//   try {
//     const res = await axios.post(
//       `${BASE_URL}warranty/customer/address/list`,
//       {
//         w_customer_id: customerId,
//       }
//     );

//     if (res.data?.status) {
//       setAddresses(res.data.data || []);
//     }
//   } catch (err) {
//     console.error(
//       "Failed to fetch addresses",
//       err?.response?.data || err.message
//     );
//   }
// };

//   const fetchCustomerDevices = async () => {
//   if (!customerId) return;

//   try {
//     setDevicesLoading(true);

//     const res = await axios.get(
//       `${BASE_URL}warranty/get-customer-details`,
//       { params: { id: customerId } }
//     );

//     const data = res.data;

//     // ✅ CASE 1: API returns SINGLE CUSTOMER OBJECT
//     if (data && !Array.isArray(data)) {
//       setCustomerInfo(data);
//       setRetailerInfo(data.retailer || null);
//       setDevices(data.devices || []);
//       return;
//     }

//     // ✅ CASE 2: API returns ARRAY of customers
//     if (Array.isArray(data)) {
//       setCustomers(data);

//       const currentCustomer = data.find(
//         (c) => Number(c.id) === Number(customerId)
//       );

//       setCustomerInfo(currentCustomer || null);
//       setRetailerInfo(currentCustomer?.retailer || null);
//       setDevices(currentCustomer?.devices || []);
//       return;
//     }

//     // ❌ fallback
//     setCustomerInfo(null);
//     setRetailerInfo(null);
//     setDevices([]);
//   } catch (err) {
//     console.error(
//       "Failed to fetch customer details",
//       err?.response?.data || err.message
//     );
//     setCustomerInfo(null);
//     setRetailerInfo(null);
//     setDevices([]);
//   } finally {
//     setDevicesLoading(false);
//   }
// };


//   const handleSaveAddress = async (payload) => {
//   try {
//     setSavingAddress(true);

//     if (addressMode === "add") {
//       // 🔹 CREATE
//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/address/create`,
//         {
//           w_customer_id: customerId,
//           ...payload,
//         }
//       );

//       if (res.data?.status) {
//         await fetchAddresses(); // 🔥 refresh from server
//         setOpenAddressDialog(false);
//       }
//     } else {
//       // 🔹 UPDATE
//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/address/update`,
//         {
//           address_id: selectedAddress.id,
//           ...payload,
//         }
//       );

//       if (res.data?.status) {
//         setAddresses((prev) =>
//           prev.map((a) =>
//             a.id === selectedAddress.id
//               ? { ...a, ...payload }
//               : a
//           )
//         );
//         setOpenAddressDialog(false);
//       }
//     }
//   } catch (err) {
//     alert(
//       err?.response?.data?.message ||
//         "Failed to save address"
//     );
//   } finally {
//     setSavingAddress(false);
//   }
// };


//   const confirmDeleteAddress = (id) => {
//   setDeleteAddressId(id);
// };

// const handleDeleteAddress = async () => {
//   if (!deleteAddressId) return;

//   try {
//     setDeleting(true);

//     await axios.post(
//       `${BASE_URL}warranty/customer/address/delete`,
//       { address_id: deleteAddressId }
//     );

//     await fetchAddresses(); // refresh list
//     setDeleteAddressId(null);
//   } catch (err) {
//     alert("Failed to delete address");
//   } finally {
//     setDeleting(false);
//   }
// };




//   /* ---------------- BOOT ---------------- */

//   useEffect(() => {
//   if (!customerId) return;

//   fetchAddresses();
//   fetchCustomerDevices();
//   fetchClaims(); // ✅ ADD THIS

//   const t = setTimeout(() => setBootLoading(false), 500);
//   return () => clearTimeout(t);
// }, [customerId]);

  


//   /* ---------------- RENDER ---------------- */

//   if (!user) return null;

//   return (
//     <>
//     {globalLoading && <GlobalLoader />}
//     <Box
//       sx={{
//         minHeight: "100vh",
//         bgcolor: "#f4f6fb",
//         display: "flex",
//         flexDirection: "column",
//       }}
//     >
//       {/* HEADER */}
//       <AppHeader user={user} onLogout={logout} />

//       {/* ROUTED CONTENT */}
//       <Box sx={{ flex: 1, p: 2,overflowY: "auto", pb: "72px", }}>
//         <Outlet
//           context={{
//             bootLoading,
//             devices,
//             claims,
//             claimsLoading,        // ✅ ADD
//             customerInfo,
//             retailerInfo,
//             addresses,
//             onRaiseClaim: startClaim,
//             onOpenClaimDetails: handleOpenClaimDetails, // ✅ ADD
//             onOpenDevice: openDeviceDetailsById,
//             onViewPurchase: openPurchaseList,
//             onAddAddress: openAddAddress,
//             onEditAddress: openEditAddress,
//             onDeleteAddress: confirmDeleteAddress,
//           }}
//         />


//       </Box>

//       {/* BOTTOM NAV (URL BASED) */}
//       <BottomNav />

//       {/* ---------------- DIALOGS ---------------- */}

//       <ClaimWizardDialog
//         open={openClaim}
//         preselectedDeviceId={preselectedDeviceId}
//         onClose={() => setOpenClaim(false)}
//         devices={devices}
//         addresses={addresses}
//         retailerInfo={retailerInfo}
//         channelPartners={[]}
//         onSubmit={async () => {
//           setOpenClaim(false);
//           await fetchClaims(); // 🔥 REFRESH CLAIM LIST
//         }}
//       />

//       <AddressDialog
//         open={openAddressDialog}
//         mode={addressMode}
//         initialData={selectedAddress}
//         saving={savingAddress}
//         onClose={() => setOpenAddressDialog(false)}
//         onSave={handleSaveAddress}
//       />

//       <PurchasesDialog
//         open={openPurchases}
//         onClose={() => setOpenPurchases(false)}
//         purchases={devices}
//         retailerInfo={retailerInfo}
//         activePurchaseId={activePurchaseId}
//       />

//       <DeviceDetailsDialog
//         open={openDeviceDetails}
//         onClose={() => setOpenDeviceDetails(false)}
//         device={activeDevice}
//         retailerInfo={retailerInfo} 
//         deviceClaims={deviceClaims}
//         onRaiseClaim={(id) => {
//           setOpenDeviceDetails(false);
//           startClaim(id);
//         }}
//         onOpenClaim={(claim) => {
//           setOpenDeviceDetails(false);
//           handleOpenClaimDetails(claim); // ✅ FIXED
//         }}

//       />

//       <ConfirmDialog
//         open={Boolean(deleteAddressId)}
//         title="Delete Address?"
//         description="This address will be permanently removed. This action cannot be undone."
//         confirmText="Delete"
//         cancelText="Cancel"
//         loading={deleting}
//         onClose={() => setDeleteAddressId(null)}
//         onConfirm={handleDeleteAddress}
//       />

//       <ClaimDetailsDialog
//         open={openClaimDetails}
//         onClose={() => setOpenClaimDetails(false)}
//         claim={activeClaim}
//         device={activeClaim?.device}   // ✅ API already sends this
//       />
//     </Box>

//     </>
//   );
// }


// function GlobalLoader() {
//   return (
//     <Box
//       sx={{
//         position: "fixed",
//         inset: 0,
//         zIndex: 2000,
//         bgcolor: "rgba(255,255,255,0.7)",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         backdropFilter: "blur(2px)",
//       }}
//     >
//       <div className="flex flex-col items-center gap-3">
//         <div className="h-10 w-10 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
//         <span className="text-sm text-slate-600 font-medium">
//           Loading, please wait…
//         </span>
//       </div>
//     </Box>
//   );
// }